import { zodResolver } from '@hookform/resolvers/zod';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useEffect } from 'react';
import type { DefaultValues } from 'react-hook-form';
import { useForm, useFormContext } from 'react-hook-form';
import { useSWRConfig } from 'swr';

import type {
  LAIUseFormReturn,
  LiftAIFormProps,
} from '~/components/uploadDocument/SelectDocumentTypeDialog';
import { useGlobalFilterContext } from '~/contexts/globalFilterContext';
import { carsPartialUpdate } from '~/data/generated/endpoints/cars/cars';
import type { Car } from '~/data/generated/model';
import { useOEMs } from '~/data/hooks/useOEMs';
import type { CarDto } from '~/data/schemas/car';
import { carDtoSchema } from '~/data/schemas/car';
import type { Expect } from '~/data/typeAssertions';
import useLAISnackbar, { ActionEnum } from '~/hooks/useLAISnackbar';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';

import { createFormInvalidHandler, FieldAndCell } from '../form/utils';
import { buildCarFieldDef } from './propertyCarFormFields';

// AMP-620: `{...getDefaultValues(), ...initialData}` below means any CarDto
// field ABSENT from `initialData` silently becomes the `getDefaultValues()`
// null/false/0 default in the form — and that default then goes out in the
// PATCH body as an explicit overwrite (`updateCar`), not a no-op. This is
// safe only as long as every `CarDto` field name also exists on `Car` (the
// generated model `initialData` is ultimately sourced from — see
// CertificatePanelHeader.tsx:110, which hands this component a `Car` forced
// to `CarDto` via `as unknown as`, defeating any check that isn't at the
// type level). This pin catches a field being renamed/removed from `Car`;
// it can't catch a field merely being *optional* on `Car` (several are,
// which is the same drf-spectacular-conservatism gap documented in
// `typeAssertions.ts` — closing that fully would need a runtime guard,
// which is out of scope here).
type _PinCarDtoFieldsExistOnCar = Expect<keyof CarDto extends keyof Car ? true : false>;

const getDefaultValues = (): DefaultValues<CarDto> => {
  return {
    ahjId: null,
    name: '',
    criticalUnit: false,
    elevatorType: null,
    installationYear: 0,
    modernizationYear: null,
    numLandings: null,
    speedFpm: null,
    capacityLbs: null,
    controllerModel: null,
    driveType: null,
    lastSspiTest: null,
    numEntrances: null,
    oem: null,
    lastCat1Test: null,
    lastCat5Test: null,
  };
};

const updateCar = async (dto: CarDto): Promise<Car> => {
  // This form only edits existing cars (PropertyEditCarDialog), so an id must be
  // present. Guard rather than fall back to '' — that would PATCH /cars//… and
  // silently hit the wrong endpoint.
  if (!dto.id) {
    throw new Error('Cannot update a car without an id');
  }
  // `dto` is the form's validated hand type (CarDto); the SDK's patched request
  // body type is generated. Cast the request body only — the response is the
  // generated `Car` and flows through untyped-cast-free.
  const { data } = await carsPartialUpdate(dto.id, dto as never);
  return data;
};

export const useCarForm = (): LAIUseFormReturn<CarDto> => {
  const hookForm = useForm<CarDto>({
    mode: 'all',
    defaultValues: getDefaultValues(),
    resolver: zodResolver(carDtoSchema),
  });

  return { hookForm, Form: CarForm, Fields: CarFields };
};

const CarForm = ({ children, onSubmit, initialData }: LiftAIFormProps<CarDto, Car>) => {
  const { mutate } = useSWRConfig();
  const { showEntityActionSnackbar, showSnackbar } = useLAISnackbar();
  const { handleSubmit, reset } = useFormContext<CarDto>();

  const {
    selectedFilters: {
      date: { start: filterStart, end: filterEnd },
    },
  } = useGlobalFilterContext();

  useEffect(() => {
    if (!initialData) {
      return;
    }

    const formValues = {
      ...getDefaultValues(),
      ...initialData,
    };
    reset(formValues);
  }, [initialData, reset]);

  return (
    <form
      noValidate
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      onSubmit={handleSubmit(
        async (data) => {
          try {
            const carDto = carDtoSchema.parse(data);
            const car = await updateCar(carDto);
            onSubmit(car);
            const action = ActionEnum.Update;

            showEntityActionSnackbar(
              {
                name: 'Car',
                action,
                id: data.id,
              },
              {
                variant: 'info',
              },
            );

            await mutate(
              ['property', car.property.id, 'cars', { startDate: filterStart, endDate: filterEnd }],
              undefined,
              { revalidate: true },
            );
          } catch (err) {
            console.error('CarForm save error: ', err);
            showSnackbar('Car failed to save', {
              variant: 'error',
            });
          }
        },
        createFormInvalidHandler<CarDto>({ formName: 'CarForm', showSnackbar }),
      )}
    >
      {children}
    </form>
  );
};

const CarFields: React.FC = () => {
  const { control } = useFormContext<CarDto>();
  const terms = useLocaleTerms();

  const { oems } = useOEMs();

  const fields = buildCarFieldDef({
    oems,
    terms,
  });

  return (
    <>
      <Typography variant="h6" my={2}>
        {terms.unit} Details
      </Typography>
      <Grid container spacing={2}>
        {fields.map((field) => (
          <FieldAndCell key={field.name} field={field} control={control} />
        ))}
      </Grid>
    </>
  );
};
